import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Paperclip, Send, RotateCcw, X, ChevronDown, Pin } from "lucide-react";
import { RunCard } from "./RunCard";
import { api, ApiError, detailText } from "./api";
import { Mark } from "./Logo";
import { Pill } from "./ui";
import { toast } from "sonner";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
type Up = { file: File; status: "queued" | "uploading" | "done" | "failed"; pct: number; key?: string; error?: string };

export const GullAvatar = ({ size = 22 }: { size?: number }) => <span className="gull-avatar" aria-hidden><Mark size={size} /></span>;

export function useThread(initial?: string | null, programmeId?: string | null) {
  const [thread, setThread] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initial) api(`/gull/threads/${initial}`).then(setThread).catch(() => setThread(null)); }, [initial]);
  const last = useRef<any>(null);  // the thread returned by the previous turn, so a follow-up in the same tick reuses the bound thread
  const ensure = async () => thread || last.current || (await api("/gull/threads", { body: { programme_id: programmeId || null } }).then((t) => { setThread(t); last.current = t; return t; }));
  const say = async (text: string, extra: any = {}) => {
    setBusy(true);
    try { const t = await ensure(); const r = await api(`/gull/threads/${t.id}/messages`, { body: { text, ...extra } }); last.current = r; setThread(r); return r; }
    catch (e) { toast.error(e instanceof ApiError ? (e.message || detailText(e.data?.detail)) : "Gull could not reply"); }
    finally { setBusy(false); }
  };
  return { thread, setThread, say, busy, ensure };
}

export function uploadOne(pid: string, u: Up, onProgress: (p: number) => void): Promise<string> {
  return new Promise((res, rej) => {
    const fd = new FormData(); fd.append("file", u.file); fd.append("title", u.file.name);
    const x = new XMLHttpRequest(); x.open("POST", `${BASE}/programmes/${pid}/sources`); x.withCredentials = true; x.timeout = 120000;
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    x.onload = () => { if (x.status < 300) { try { res(JSON.parse(x.responseText).record.key); } catch { rej(new Error("bad response")); } } else { let d = x.statusText; try { d = detailText(JSON.parse(x.responseText).detail); } catch {} rej(new Error(`${x.status}: ${d}`)); } };
    x.onerror = () => rej(new Error("network error")); x.ontimeout = () => rej(new Error("timed out after 120 s"));
    x.send(fd);
  });
}

export function FileList({ ups, retry, remove }: { ups: Up[]; retry: (i: number) => void; remove: (i: number) => void }) {
  if (!ups.length) return null;
  const done = ups.filter((u) => u.status === "done").length;
  return (
    <div className="gull-files" data-testid="gull-files">
      <div className="tiny muted">{done}/{ups.length} processed{ups.some((u) => u.status === "failed") ? " · some failed" : ""}</div>
      {ups.map((u, i) => <div className="gull-file" key={i} data-testid={`gull-file-${i}`}>
        <Paperclip size={13} /><span className="grow ellipsis" title={u.file.name}>{u.file.name}</span>
        {u.status === "uploading" && <progress value={u.pct} max={100} aria-label={`Uploading ${u.file.name}`} aria-valuenow={u.pct} />}
        <Pill s={u.status === "done" ? "COMPLETE" : u.status === "failed" ? "FAILED" : u.status === "uploading" ? "RUNNING" : "PENDING"}>{u.status}</Pill>
        {u.status === "failed" && <><small className="muted">{u.error}</small><button className="ghost" onClick={() => retry(i)} aria-label={`Retry ${u.file.name}`} data-testid={`gull-file-retry-${i}`}><RotateCcw size={13} /></button></>}
        {u.status !== "uploading" && <button className="ghost" onClick={() => remove(i)} aria-label={`Remove ${u.file.name}`}><X size={13} /></button>}
      </div>)}
    </div>
  );
}

export function GullComposer({ pid, placeholder, onSent, say, busy, compact }: { pid?: string | null; placeholder: string; onSent?: (r: any) => void; say: (t: string, x?: any) => Promise<any>; busy: boolean; compact?: boolean }) {
  const [text, setText] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    const pending = sessionStorage.getItem("larus.gull.prefill"); if (pending) { setText(pending); sessionStorage.removeItem("larus.gull.prefill"); }
    const h = (e: any) => setText(e.detail || ""); window.addEventListener("larus:gull-prefill", h); return () => window.removeEventListener("larus:gull-prefill", h);
  }, []);
  const [ups, setUps] = useState<Up[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const add = (fs: FileList | null) => fs && setUps((u) => [...u, ...Array.from(fs).map((file) => ({ file, status: "queued" as const, pct: 0 }))]);
  const runUploads = async (target: string) => {
    const keys: string[] = [];
    for (let i = 0; i < ups.length; i++) {
      const u = ups[i];
      if (u.status === "done" && u.key) { keys.push(u.key); continue; }
      setUps((a) => a.map((x, j) => (j === i ? { ...x, status: "uploading", pct: 0, error: undefined } : x)));
      try { const k = await uploadOne(target, u, (pct) => setUps((a) => a.map((x, j) => (j === i ? { ...x, pct } : x)))); keys.push(k); setUps((a) => a.map((x, j) => (j === i ? { ...x, status: "done", key: k, pct: 100 } : x))); }
      catch (e: any) { setUps((a) => a.map((x, j) => (j === i ? { ...x, status: "failed", error: e.message } : x))); }
    }
    return keys;
  };
  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!text.trim() && !ups.length) return;
    const msg = text.trim();
    let target = pid || null;
    let r: any; let first: any = null;
    if (ups.length && target) {
      const keys = await runUploads(target);  // uploads first, then one turn carrying the sourced keys
      r = await say(msg || "Review the attached documents", keys.length ? { source_keys: keys, action: msg ? undefined : "review_sources" } : {});
    } else {
      r = await say(msg || "Review the attached documents");
      if (!r) return;
      first = r;
      target = target || r.programme_id;
      if (ups.length && target) { const keys = await runUploads(target); if (keys.length) r = await say("", { action: "review_sources", source_keys: keys }); }
      else if (ups.length) toast.error("Start or choose a programme before attaching documents");
    }
    if (!r) return;
    setUps((a) => a.filter((u) => u.status === "failed"));  // processed files leave the composer; failed ones stay for retry
    setText("");
    onSent?.(r);
    const ctls = [...((first?.messages || []).slice(-1)[0]?.controls || []), ...((r.messages || []).slice(-1)[0]?.controls || []), ...(r.controls || [])];
    const handoff = ctls.find((c: any) => c.handoff === "review");
    if (handoff && /\breview\b/i.test(msg)) navigate(handoff.to);  // the user asked to be taken to source review: open the real object
  };
  return (
    <form className={`gull-composer ${compact ? "compact" : ""}`} onSubmit={submit} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }} data-testid="gull-composer">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 2 : 3} placeholder={placeholder} aria-label="Ask Gull" data-testid="gull-input"
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(); }} />
      <FileList ups={ups} retry={(i) => setUps((a) => a.map((x, j) => (j === i ? { ...x, status: "queued", error: undefined } : x)))} remove={(i) => setUps((a) => a.filter((_, j) => j !== i))} />
      <div className="gull-bar">
        <input ref={fileRef} type="file" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="gull-file-input" accept=".txt,.md,.csv,.json,.pdf,.docx" />
        <button type="button" className="ghost" onClick={() => fileRef.current?.click()} data-testid="gull-attach-btn"><Paperclip size={14} />Attach documents</button>
        <span className="tiny muted grow">Files go through the existing upload, quarantine and rights controls · 10 MB each</span>
        <button className="primary" disabled={busy || (!text.trim() && !ups.length)} data-testid="gull-send-btn"><Send size={14} />{busy ? "Working…" : "Send to Gull"}</button>
      </div>
    </form>
  );
}

export function GullMessages({ thread, pid, onPinned }: { thread: any; pid?: string | null; onPinned?: () => void }) {
  const [trace, setTrace] = useState<string | null>(null);
  const [pinned, setPinned] = useState<Record<string, string>>({});
  if (!thread?.messages?.length) return null;
  const prog = pid || thread.programme_id;
  const pin = async (m: any) => {
    try { const r = await api(`/gull/threads/${thread.id}/messages/${m.id}/pin`, { body: { note: "" } }); setPinned((p) => ({ ...p, [m.id]: r.key })); toast.success("Pinned to the programme Record as a reasoning note"); onPinned?.(); }
    catch (e) { toast.error(e instanceof ApiError ? e.message : "Could not pin"); }
  };
  return (
    <div className="gull-thread" data-testid="gull-thread" role="log" aria-live="polite" aria-label="Conversation with Gull">
      {thread.messages.map((m: any) => (
        <article key={m.id} className={`gull-msg ${m.role}`} data-testid={`gull-msg-${m.role}`} aria-label={m.role === "gull" ? "Gull" : "You"}>
          {m.role === "gull" ? <GullAvatar /> : <span className="gull-you" aria-hidden>You</span>}
          <div className="grow">
            {m.content.split("\n").map((ln: string, i: number) => <p key={i} className={ln.startsWith("Held by policy") ? "held" : ln.startsWith("Need from you") ? "need" : ln.startsWith("Planner:") ? "tiny muted" : ""}>{ln}</p>)}
            {m.role === "gull" && prog && (m.usage?.saved || []).filter((s: any) => s.type === "run").map((s: any) => <RunCard key={s.key} runId={s.key} pid={prog} />)}
            {m.role === "gull" && m.plan?.receipt?.length ? <ul className="gull-receipt" data-testid="gull-receipt" aria-label="Request receipt">{m.plan.receipt.map((it: any) => <li key={it.n} data-testid={`gull-receipt-${it.n}`}><Pill s={it.status === "ANSWERED" ? "COMPLETE" : it.status === "QUEUED" ? "RUNNING" : it.status === "BLOCKED" || it.status === "FAILED" ? "FAILED" : "PENDING"}>{it.status.toLowerCase()}</Pill><span>{it.kind.replace(/_/g, " ")}{it.request && it.kind !== it.request ? <small className="muted"> · {it.request.slice(0, 90)}</small> : null}</span></li>)}</ul> : null}
            {m.controls?.length ? <div className="gull-controls">{m.controls.map((c: any, i: number) => <Link key={i} to={c.to} className="gull-ctl" data-testid={`gull-ctl-${i}`}>{c.label} →</Link>)}</div> : null}
            {m.role === "gull" && <div className="gull-meta">
              <button className="ghost tiny" onClick={() => setTrace(trace === m.id ? null : m.id)} aria-expanded={trace === m.id} data-testid={`gull-trace-btn-${m.id}`}><ChevronDown size={12} aria-hidden /> {m.plan?.planner === "MODEL" ? `Model plan · ${m.usage?.model || ""} · ${m.usage?.input_tokens ?? "?"} in / ${m.usage?.output_tokens ?? "?"} out` : m.plan?.planner || "plan"} · {m.tool_calls?.length || 0} tool calls</button>
              {prog && (pinned[m.id] ? <span className="tiny muted" data-testid={`gull-pinned-${m.id}`}><Pin size={12} aria-hidden /> Pinned to Record</span>
                : <button className="ghost tiny" onClick={() => pin(m)} aria-label="Pin this answer to the programme Record" data-testid={`gull-pin-btn-${m.id}`}><Pin size={12} aria-hidden /> Pin to Record</button>)}
            </div>}
            {trace === m.id && <pre className="gull-trace" data-testid="gull-trace">{JSON.stringify({ plan: m.plan, tool_calls: m.tool_calls, usage: m.usage }, null, 1)}</pre>}
          </div>
        </article>
      ))}
    </div>
  );
}
